/* src/components/sessions/DateFilterControls.js */
import React, { useState, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCalendarDays, 
  faFilterCircleXmark, 
  faCheck, 
  faTimes,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';

// Helper to format date to readable format
const formatDateForDisplay = (date) => {
    if (!date) return '';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return date.toLocaleDateString(undefined, options);
};

const DateFilterControls = ({ onFilterChange, initialStartDate = null, initialEndDate = null }) => {
    const [startDate, setStartDate] = useState(initialStartDate);
    const [endDate, setEndDate] = useState(initialEndDate);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const datePickerRef = useRef(null);

    // Update local state if initial props change (e.g., reset externally)
    useEffect(() => {
         setStartDate(initialStartDate);
         setEndDate(initialEndDate);
    }, [initialStartDate, initialEndDate]);

    // Add click outside listener to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
                setShowDatePicker(false);
            }
        };

        if (showDatePicker) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDatePicker]);

    const handleApplyFilter = () => {
        onFilterChange(startDate, endDate);
        setShowDatePicker(false);
    };

    const handleClearFilter = () => {
        setStartDate(null);
        setEndDate(null);
        onFilterChange(null, null);
        setShowDatePicker(false);
    };

    const handleDateChange = (dates) => {
        const [start, end] = dates;
        setStartDate(start);
        setEndDate(end);
    };

    const isFiltersActive = startDate || endDate;

    return (
        <div className="relative w-full sm:w-auto flex flex-col sm:flex-row items-start sm:items-center gap-2">
             {/* Button to toggle Date Picker Popover */}
             <button
                onClick={() => setShowDatePicker(!showDatePicker)}
                className={`flex items-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 w-full sm:w-auto ${
                    isFiltersActive 
                        ? 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100' 
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
                aria-expanded={showDatePicker}
                aria-controls="date-picker-dropdown"
            >
                <FontAwesomeIcon icon={faCalendarDays} className={`mr-2 h-4 w-4 ${isFiltersActive ? 'text-indigo-500' : 'text-gray-500'}`} />
                {isFiltersActive ? (
                     <span className="truncate">
                          {formatDateForDisplay(startDate)} - {formatDateForDisplay(endDate) || 'Present'}
                     </span>
                ) : (
                    'Filter by Date'
                )}
            </button>

            {/* Clear Button - Visible only when a filter is active */}
            {isFiltersActive && (
                 <button
                    onClick={handleClearFilter}
                    className="flex items-center px-3 py-2 bg-red-50 border border-red-200 rounded-md shadow-sm text-sm font-medium text-red-600 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-2"
                    title="Clear Date Filter"
                >
                    <FontAwesomeIcon icon={faFilterCircleXmark} className="h-4 w-4 mr-1" />
                    <span className="hidden sm:inline">Clear</span>
                </button>
            )}

            {/* Date Picker Popover */}
            {showDatePicker && (
                <div 
                    ref={datePickerRef}
                    className="absolute top-full left-0 mt-2 z-10 bg-white border border-gray-300 rounded-lg shadow-lg p-4 w-full sm:w-auto"
                    id="date-picker-dropdown"
                >
                    <div className="mb-3 border-b border-gray-200 pb-2">
                        <h3 className="text-sm font-medium text-gray-700 mb-1">Select Date Range</h3>
                        <p className="text-xs text-gray-500">Filter sessions by their completion date</p>
                    </div>
                    
                    <div className="bg-gray-50 p-3 rounded-md border border-gray-200 mb-3">
                        <div className="h-6 flex justify-center mb-1">
                            {(startDate || endDate) && (
                                <div className="text-xs font-medium text-gray-700">
                                    {startDate ? formatDateForDisplay(startDate) : ""} 
                                    {endDate ? ` - ${formatDateForDisplay(endDate)}` : (startDate ? " - Present" : "")}
                                </div>
                            )}
                        </div>
                        <DatePicker
                            selected={startDate}
                            onChange={handleDateChange}
                            startDate={startDate}
                            endDate={endDate}
                            selectsRange
                            inline
                            maxDate={new Date()}
                            calendarClassName="bg-white shadow-sm rounded-md"
                        />
                    </div>
                    
                    <div className="flex justify-between items-center mt-3">
                        <div className="text-xs text-gray-500 w-1/3 h-5">
                            {startDate && (
                                <span className="text-xs">
                                    {startDate && !endDate && "Select end date"}
                                </span>
                            )}
                        </div>
                        
                        <div className="flex space-x-2">
                            <button
                                onClick={() => setShowDatePicker(false)}
                                className="px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-gray-500 min-w-[80px]"
                            >
                                <FontAwesomeIcon icon={faTimes} className="mr-1.5" />
                                Cancel
                            </button>
                            
                            <button
                                onClick={handleApplyFilter}
                                className="px-3 py-1 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700 disabled:opacity-50 focus:outline-none focus:ring-1 focus:ring-indigo-500 flex items-center justify-center min-w-[80px]"
                                disabled={!startDate}
                            >
                                <FontAwesomeIcon icon={faCheck} className="mr-1.5" />
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DateFilterControls;